import { browserStorage, defineStorageEntry, stringStorageCodec } from "@/lib/browserStorage";
import { requestTenancySelection, TenancyRequiredError } from "./recovery";

const activeTenancyEntry = defineStorageEntry({
  area: "session",
  key: "decisively.tenant",
  codec: stringStorageCodec,
});

const activeTenancyIdentityEntry = defineStorageEntry({
  area: "session",
  key: "decisively.tenantIdentity",
  codec: stringStorageCodec,
});

// Compatibility value for the initial tab-storage rollout. New code reads the
// identity-scoped entry after the authenticated identity has been established.
const legacyLastTenancyEntry = defineStorageEntry({
  area: "local",
  key: "decisively.lastTenant",
  codec: stringStorageCodec,
});

const legacyLastTenancyIdentityEntry = defineStorageEntry({
  area: "local",
  key: "decisively.lastTenantIdentity",
  codec: stringStorageCodec,
});

// Read-only compatibility entry for the initial migration. Deliberately left intact.
const legacyActiveTenancyEntry = defineStorageEntry({
  area: "local",
  key: "decisively.tenant",
  codec: stringStorageCodec,
});

const buildLastTenancyEntry = (identity: string) =>
  defineStorageEntry({
    area: "local",
    key: `decisively.lastTenant:${encodeURIComponent(identity)}`,
    codec: stringStorageCodec,
  });

const normalizeTenancy = (tenancy: string | null): string | null => {
  const normalized = tenancy?.trim();
  return normalized ? normalized : null;
};

const normalizeIdentity = (identity: string | null): string | null => {
  const normalized = identity?.trim();
  return normalized ? normalized : null;
};

const getActiveTenancyIdentity = (): string | null =>
  normalizeIdentity(browserStorage.get(activeTenancyIdentityEntry));

const rememberLastTenancy = (identity: string, tenancy: string): void => {
  browserStorage.set(buildLastTenancyEntry(identity), tenancy);

  // Keep the rollout key current for older portal versions, but bind it to an
  // identity so a different user never consumes it as their own preference.
  browserStorage.set(legacyLastTenancyEntry, tenancy);
  browserStorage.set(legacyLastTenancyIdentityEntry, identity);
};

const getLastTenancy = (identity: string): string | null => {
  const scopedTenancy = normalizeTenancy(browserStorage.get(buildLastTenancyEntry(identity)));
  if (scopedTenancy) {
    return scopedTenancy;
  }

  const migrationIdentity = normalizeIdentity(browserStorage.get(legacyLastTenancyIdentityEntry));
  if (migrationIdentity && migrationIdentity !== identity) {
    return null;
  }

  const legacyLastTenancy = normalizeTenancy(browserStorage.get(legacyLastTenancyEntry));
  const legacyActiveTenancy = normalizeTenancy(browserStorage.get(legacyActiveTenancyEntry));
  const tenancyToMigrate = legacyLastTenancy ?? legacyActiveTenancy;
  if (!tenancyToMigrate) {
    return null;
  }

  if (!migrationIdentity && !browserStorage.set(legacyLastTenancyIdentityEntry, identity)) {
    return null;
  }
  if (!browserStorage.set(buildLastTenancyEntry(identity), tenancyToMigrate)) {
    return null;
  }
  if (!legacyLastTenancy) {
    browserStorage.set(legacyLastTenancyEntry, tenancyToMigrate);
  }
  return tenancyToMigrate;
};

/**
 * Establishes which authenticated OIDC identity owns the tab's tenancy state.
 * Silent refreshes for the same identity preserve state; identity changes clear
 * the tab selection before considering that user's own last-used tenancy.
 */
export const setActiveTenancyIdentity = (identity: string): boolean => {
  const normalizedIdentity = normalizeIdentity(identity);
  if (!normalizedIdentity) {
    return false;
  }

  const existingIdentity = getActiveTenancyIdentity();
  if (existingIdentity === normalizedIdentity) {
    return true;
  }

  const migrationIdentity = normalizeIdentity(browserStorage.get(legacyLastTenancyIdentityEntry));
  const canBindLegacyTabState = !existingIdentity && (!migrationIdentity || migrationIdentity === normalizedIdentity);

  if (!canBindLegacyTabState) {
    const tenancyCleared = browserStorage.remove(activeTenancyEntry);
    const identityCleared = browserStorage.remove(activeTenancyIdentityEntry);
    if (!tenancyCleared || !identityCleared) {
      return false;
    }
  }
  if (!browserStorage.set(activeTenancyIdentityEntry, normalizedIdentity)) {
    return false;
  }

  return true;
};

/**
 * Returns the tab's active tenancy, seeding a new tab from last-used state. The
 * legacy localStorage key is read only for the rollout migration and is never
 * treated as live active state.
 */
export const getActiveTenancy = (): string | null => {
  const identity = getActiveTenancyIdentity();
  if (!identity) {
    return null;
  }

  const activeTenancy = normalizeTenancy(browserStorage.get(activeTenancyEntry));
  if (activeTenancy) {
    return activeTenancy;
  }

  const initialTenancy = getLastTenancy(identity);
  if (!initialTenancy || !browserStorage.set(activeTenancyEntry, initialTenancy)) {
    return null;
  }
  return initialTenancy;
};

export const setActiveTenancy = (tenancy: string): boolean => {
  const normalized = normalizeTenancy(tenancy);
  const identity = getActiveTenancyIdentity();
  if (!identity || !normalized || !browserStorage.set(activeTenancyEntry, normalized)) {
    return false;
  }

  // The tab-scoped value is authoritative. A failed last-used write must not
  // invalidate a tenancy that was safely stored for the current tab.
  rememberLastTenancy(identity, normalized);
  return true;
};

export const clearActiveTenancy = (): boolean => browserStorage.remove(activeTenancyEntry);

/** Clears only tab-owned identity state; per-user last-used preferences remain. */
export const clearTenancyContext = (): boolean => {
  const tenancyCleared = browserStorage.remove(activeTenancyEntry);
  const identityCleared = browserStorage.remove(activeTenancyIdentityEntry);
  return tenancyCleared && identityCleared;
};

export const requireActiveTenancy = (): string => {
  const tenancy = getActiveTenancy();
  if (!tenancy) {
    requestTenancySelection();
    throw new TenancyRequiredError();
  }
  return tenancy;
};
